import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFormik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { createSurvey, surveyDetail, updateSurvey } from 'api/endpoints/survey';
import { getImages, uploadImages } from 'api/endpoints/upload';
import Breadcrumb from 'components/Breadcrumb';
import Button from 'components/Button';
import LayoutReview from 'components/layouts/LayoutReview';
import UndoRedo from 'components/UndoRedo';
import { ENUM_QUESTION_TYPE, ENUM_SURVEY_STATUS, ENUM_SURVEY_TYPE, ENUM_TYPE_ACTION } from 'constants/enum';
import LOCAL_STORAGE_KEYS from 'constants/localStorageKeys';
import { TEXT_COMMON, TEXT_CREATE_SURVEY } from 'constants/string/texts';
import useChangeRouter from 'hooks/useChangeRouter';
import { useClickOutside } from 'hooks/useClickOutside';
import useReloadPage from 'hooks/useReloadPage';
import { toastError, toastSuccess } from 'lib/toast';
import AppScreenReview from 'pages/Survey/_components/AppScreenReview';
import Duplicate from 'pages/Survey/_components/Duplicate';
import Form from 'pages/Survey/Form/Form';
import AppRoutes from 'routes/routes';
import { clearModal, setLoading, setModal } from 'store/actions/common';
import { clearCreateSurvey, setHistoryCreateSurvey } from 'store/actions/survey';
import { base64ToFile, blobToBase64, convertStatusSurvey, getImageDimensions, scrollToPosition, scrollToSection } from 'utils/helper';
import { removeFromLocalStorage } from 'utils/localStorage';

const MoreVert = require('assets/resources/MoreVert.svg').default;

const CreateSurvey = () => {
  const navigator = useNavigate();
  const dispatch = useDispatch();
  const { me } = useSelector((state) => state.auth);
  const [sectionFocusing, setSectionFocusing] = useState(-1);
  const [questionFocusing, setQuestionFocusing] = useState({});
  const [currentStatus, setCurrentStatus] = useState(ENUM_SURVEY_STATUS.DRAFT);
  const controlSurveyRef = useRef(null);
  const [openControlSurvey, setIsOpenControlSurvey] = useState(false);
  const [openModalDuplicate, setOpenModalDuplicate] = useState(false);
  const [dataNormal, setDataNormal] = useState({});
  const { id } = useParams();
  const { goto } = useChangeRouter();

  useReloadPage('');
  useClickOutside(controlSurveyRef, () => setIsOpenControlSurvey(false));

  const breadcrumbs = [
    {
      title: TEXT_CREATE_SURVEY.SURVEY_LIST,
      event: () => goto(AppRoutes.home),
    },
    {
      title: TEXT_CREATE_SURVEY.NEW_SURVEY,
    },
  ];

  const initialValues = (() => {
    const initData = {
      title: '',
      description: '',
      status: ENUM_SURVEY_STATUS.DRAFT,
      survey_type: ENUM_SURVEY_TYPE.EXAM,
      period_type: null,
      sections: [
        {
          title: '',
          order: 0,
          questions: [],
        },
      ],
    };

    return initData;
  })();

  const handleUploadImage = async (values) => {
    /**
     * Handle image upload
     */
    const formData = new FormData();
    const section_ids = [];
    const question_ids = [];
    await values.sections.map(async (section, sectionIndex) => {
      await section.questions.map(async (question, questionIndex) => {
        if (question.images?.length > 0) {
          const file = await base64ToFile(question.images[0].image_url, `s-${sectionIndex}-q-${questionIndex}-${moment().unix()}`);
          formData.append('files', file);
          section_ids.push(section.id);
          question_ids.push(question.id);
        }
      });
    });

    let images = [];
    if (section_ids.length > 0) {
      formData.append('section_ids', section_ids.join(','));
      formData.append('question_ids', question_ids.join(','));
      images = await uploadImages(formData)
        .then(async (res) => {
          return res.data;
        })
        .catch(() => {
          return [];
        });
    }

    return images;
  };

  const handleUpdateSurvey = async (images) => {
    const handleGetImages = (question, sectionId, questionId) => {
      return [
        {
          bucket_name: _.find(images, (img) => img.section_index === sectionId && img.question_index === questionId)?.bucket_name,
          image_url: _.find(images, (img) => img.section_index === sectionId && img.question_index === questionId)?.blob_name,
          image_layout: question?.images[0]?.image_layout,
        },
      ];
    };

    /**
     * Handle update survey
     */
    const data = {
      ...form.values,
      admin_id: me.id,
      sections: form.values.sections.map((section, sectionIndex) => {
        return {
          ...section,
          order: sectionIndex,
          questions: section.questions.map((question, questionIndex) => {
            let actionQuestion =
              question.action === ENUM_TYPE_ACTION.DELETE
                ? question.action
                : section.action === ENUM_TYPE_ACTION.DELETE
                  ? ENUM_TYPE_ACTION.DELETE
                  : question.action;
            let options = [];

            if ([ENUM_QUESTION_TYPE.CHECKBOX, ENUM_QUESTION_TYPE.RADIO, ENUM_QUESTION_TYPE.DROPDOWN].includes(question.question_type)) {
              options = question.options.map((option) => ({
                ...option,
                action: option.id ? actionQuestion : ENUM_TYPE_ACTION.CREATE,
              }));
            }

            return {
              ...question,
              action: actionQuestion,
              order: questionIndex,
              ...(options.length > 0 ? { options: options } : {}),
              ...(question.images && question.images.length > 0
                ? {
                    images: handleGetImages(question, section.id, question.id),
                  }
                : {}),
            };
          }),
        };
      }),
    };

    const handleScrollToSectionError = (field_names) => {
      const firstError = field_names[0]?.replace(/body\./g, '');

      function scrollToIdentifier(prefix, identifier) {
        scrollToSection(`${prefix}-${identifier}`);
      }

      // Check for 'questions' or 'sections' and scroll accordingly
      const identifyAndScroll = (error, id, prefixMap) => {
        let idString = '';
        prefixMap.map((prefix) => {
          const start = error.indexOf(prefix) + `${prefix}.`.length;
          const end = error.indexOf('.', start);
          idString += error.substring(start, end);
        });

        scrollToIdentifier(id, idString);
        return true;
      };

      if (firstError) {
        // body.sections.0.questions.0.title
        if (firstError.includes('questions.')) {
          return identifyAndScroll(firstError, 'questions', ['sections', 'questions']);
        }

        // body.sections.1.title
        // body.sections.1.questions
        if (firstError.includes('sections')) {
          return identifyAndScroll(firstError, 'sections', ['sections']);
        }

        if (firstError.includes('title')) {
          scrollToPosition(0);
          return true;
        }
      }
    };
    if (currentStatus === ENUM_SURVEY_STATUS.PRIVATE) {
      await createSurvey(data)
        .then((response) => {
          toastSuccess(response.message);
          removeFromLocalStorage(LOCAL_STORAGE_KEYS.HISTORY_CREATE_SURVEY_FORM);
          navigator(AppRoutes.survey);
        })
        .catch((error) => {
          toastError(error.message);
          form.setFieldValue('status', currentStatus);
          if (error.field_names) {
            handleScrollToSectionError(error?.field_names || []);
            error.field_names?.map((errorField) => {
              form.setFieldError(errorField.replace(/body./g, ''), true);
            });
          }
        });
    } else {
      await updateSurvey(id, {
        ...data,
        status:
          currentStatus === ENUM_SURVEY_STATUS.DRAFT
            ? data.status === ENUM_SURVEY_STATUS.PUBLIC
              ? ENUM_SURVEY_STATUS.DRAFT
              : data.status
            : data.status,
      })
        .then((response) => {
          removeFromLocalStorage(LOCAL_STORAGE_KEYS.HISTORY_CREATE_SURVEY_FORM);
          if (currentStatus === ENUM_SURVEY_STATUS.DRAFT && data.status === ENUM_SURVEY_STATUS.PUBLIC) {
            navigator(AppRoutes.surveyViewFn(response.data.survey_id), { state: { openSchedule: true } });
          } else {
            toastSuccess(response.message);
            navigator(AppRoutes.surveyViewFn(response.data.survey_id));
          }
        })
        .catch((error) => {
          toastError(error.message);
          form.setFieldValue('status', currentStatus);
          if (error.status_code === 400) {
            form.setFieldError('period_type', true);
          }
          if (error.field_names) {
            handleScrollToSectionError(error?.field_names || []);
            error.field_names?.map((errorField) => {
              form.setFieldError(errorField.replace(/body./g, ''), true);
            });
          }
        });
    }
  };

  const form = useFormik({
    initialValues: initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validate: () => {
      return {};
    },
    onSubmit: async (values) => {
      try {
        dispatch(setLoading({ isOpen: true }));
        const images = await handleUploadImage(values);
        await handleUpdateSurvey(images);
        dispatch(setLoading({ isOpen: false }));
      } catch {
        dispatch(setLoading({ isOpen: false }));
      }
    },
  });

  const onClickSave = (status) => {
    form.setFieldValue('status', status);
    form.handleSubmit();
  };

  const onClickDelete = () => {
    const confirmDelete = () => {
      dispatch(clearCreateSurvey());
      const newData = _.cloneDeep(dataNormal);
      form.setValues(newData);
      dispatch(clearModal());
    };

    dispatch(
      setModal({
        isOpen: true,
        type: 'delete',
        title: TEXT_CREATE_SURVEY.CONFIRM_DELETE_SURVEY,
        confirmText: TEXT_COMMON.DELETE,
        cancelText: TEXT_COMMON.CANCEL_TEXT,
        onConfirm: confirmDelete,
        onCancel: () => {
          dispatch(clearModal());
        },
      })
    );
  };

  const getSurveyDetail = useCallback(async () => {
    if (me) {
      dispatch(setLoading({ isOpen: true }));
      const result = await surveyDetail(id);
      if (result.data) {
        if (result.data.status === ENUM_SURVEY_STATUS.PUBLIC) {
          window.location.replace(AppRoutes.home);
        }
        const dataSection = [];
        const sortedSections = _.sortBy(result.data.sections, 'order');

        for (const section of sortedSections) {
          const processedQuestions = [];

          for (const question of section.questions) {
            const processedImages = [];

            for (const image of question.images || []) {
              if (image && image.signed_url) {
                const imagesBlob = await getImages(image.signed_url, image.etag);
                const url_preview = URL.createObjectURL(imagesBlob);
                const image_url = await blobToBase64(imagesBlob);
                const dimensions = await getImageDimensions(imagesBlob);

                processedImages.push({
                  ...image,
                  size: dimensions,
                  image_url: image_url,
                  url_preview: url_preview,
                });
              } else {
                processedImages.push({});
              }
            }

            let options = [];
            if ([ENUM_QUESTION_TYPE.CHECKBOX, ENUM_QUESTION_TYPE.RADIO, ENUM_QUESTION_TYPE.DROPDOWN].includes(question.question_type)) {
              options = question.options.map((option) => ({ ...option, action: ENUM_TYPE_ACTION.UPDATE }));
            }

            processedQuestions.push({
              ...question,
              action: ENUM_TYPE_ACTION.UPDATE,
              images: processedImages,
              options: options,
            });
          }

          dataSection.push({
            ...section,
            action: ENUM_TYPE_ACTION.UPDATE,
            questions: processedQuestions,
          });
        }

        setCurrentStatus(result.data.status);
        form.setValues({ ...result.data, sections: dataSection });
        setDataNormal(_.cloneDeep({ ...result.data, sections: dataSection }));
        dispatch(setHistoryCreateSurvey([JSON.stringify({ ...result.data, sections: dataSection })]));
      }
      dispatch(setLoading({ isOpen: false }));
    }
  }, [me]);

  useEffect(() => {
    getSurveyDetail();
  }, [getSurveyDetail]);

  useEffect(() => {
    return () => {
      dispatch(clearCreateSurvey());
    };
  }, []);

  const handleClickUndoRedo = () => {
    setSectionFocusing(-1);
    setQuestionFocusing({});
  };

  const onClickDuplicate = async () => {
    setOpenModalDuplicate(true);
  };

  return (
    <>
      <section id="create-survey" className="flex-middle-start gap-6 relative">
        <LayoutReview className="z-2">
          <form className="create-survey">
            <div id="header-form" className="flex-between-center mb-8 relative z-2">
              <div className="flex items-center gap-2">
                <Breadcrumb breadcrumbs={breadcrumbs} />
                <span
                  className={`text-xs rounded-sm flex-middle-center border border-solid min-w-13 min-h-5.75 ${convertStatusSurvey(form.values.status)?.class}`}
                >
                  {convertStatusSurvey(form.values.status).name}
                </span>
              </div>

              <div className="flex items-center gap-8">
                <UndoRedo onClick={handleClickUndoRedo} form={form} />

                <div className="flex items-center gap-4">
                  {form.values.status === ENUM_SURVEY_STATUS.PRIVATE && (
                    <Button
                      type="button"
                      color="outline"
                      className="text-black/85 text-sm w-26 h-8"
                      onClick={() => onClickSave(ENUM_SURVEY_STATUS.PUBLIC)}
                      text={TEXT_COMMON.REVERT_PUBLIC}
                    />
                  )}
                  {form.values.status === ENUM_SURVEY_STATUS.DRAFT && (
                    <>
                      <Button
                        type="button"
                        color="outline"
                        className="text-black/85 text-sm w-20 h-8"
                        onClick={() => onClickSave(ENUM_SURVEY_STATUS.PUBLIC)}
                        text={TEXT_CREATE_SURVEY.SAVE_PUBLIC}
                      />
                      <Button
                        type="button"
                        color="primary"
                        className="text-sm w-20 h-8"
                        onClick={() => onClickSave(ENUM_SURVEY_STATUS.DRAFT)}
                        text={TEXT_CREATE_SURVEY.SAVE}
                      />
                    </>
                  )}

                  <div className="relative flex items-center">
                    <button type="button" className="w-6 h-8" onClick={() => setIsOpenControlSurvey((prev) => !prev)} ref={controlSurveyRef}>
                      <img src={MoreVert} alt="more vert icon" width={24} height={24} />
                    </button>
                    <div className={`absolute right-0 py-1 bg-white shadow-soft-md animation-dropdown ${openControlSurvey ? 'show' : ''}`}>
                      <ul>
                        <li
                          onClick={onClickDuplicate}
                          className="px-3 w-24 py-1.75 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                        >
                          {TEXT_COMMON.DUPLICATE}
                        </li>
                        <li
                          onClick={onClickDelete}
                          className="px-3 w-24 py-1.75 text-danger-229 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                        >
                          {TEXT_COMMON.DELETE}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Form
              isEdit={true}
              form={form}
              sectionFocusing={sectionFocusing}
              setSectionFocusing={setSectionFocusing}
              questionFocusing={questionFocusing}
              setQuestionFocusing={setQuestionFocusing}
            />
          </form>
        </LayoutReview>
        <div className="sticky top-header-padding">
          <AppScreenReview form={form} />
        </div>
      </section>
      {openModalDuplicate && (
        <Duplicate
          form={form.values}
          close={() => {
            setOpenModalDuplicate(false);
          }}
        />
      )}
    </>
  );
};

export default React.memo(CreateSurvey);
